import React, { useEffect, useRef, useState } from 'react';
import { Autocomplete, CircularProgress, TextField } from '@mui/material';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import styles from './styles.module.scss';
import { displayErrorMessage } from '@utils/displayErrorMessage';


const getAddressLabel = (address) => address?.address ?? address?.fullAddress ?? '';

const DomainAddressAutocomplete = ({
  autocompleteTestId,
  className,
  disabled,
  getSuggestionsAction,
  inputTestId,
  onChange,
  onInputChange,
  onLoadingChange,
  placeholder,
  searchErrorMessage,
  textFieldClassName,
  textFieldProps,
  value,
}) => {
  const dispatch = useDispatch();
  const latestSearch = useRef('');
  const previousValue = useRef(value);
  const [inputValue, setInputValue] = useState(getAddressLabel(value));
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [options, setOptions] = useState([]);

  const updateLoading = (nextValue) => {
    setIsLoading(nextValue);
    if (onLoadingChange) onLoadingChange(nextValue);
  };

  useEffect(() => {
    if (previousValue.current !== value) {
      const nextInputValue = getAddressLabel(value);
      latestSearch.current = nextInputValue;
      setInputValue(nextInputValue);
      setOptions([]);
      setIsOpen(false);
      updateLoading(false);
    }
    previousValue.current = value;
  }, [value]); //eslint-disable-line

  const search = (searchString) => {
    latestSearch.current = searchString;
    setInputValue(searchString);
    if (onInputChange) onInputChange(searchString);

    if (!searchString.length) {
      setOptions([]);
      setIsOpen(false);
      updateLoading(false);
      return;
    }

    setIsOpen(true);

    if (searchString.length < 3) {
      setOptions([]);
      updateLoading(false);
      return;
    }

    setOptions([]);
    updateLoading(true);
    dispatch(getSuggestionsAction({
      address: searchString,
      onSuccess: (data) => {
        if (latestSearch.current !== searchString) return;
        setOptions(data?.slice?.(0, 20) ?? []);
        updateLoading(false);
        setIsOpen(true);
      },
      onError: (data) => {
        if (latestSearch.current !== searchString) return;
        displayErrorMessage(data, searchErrorMessage);
        updateLoading(false);
        setIsOpen(false);
      }
    }));
  };

  const handleInputChange = (event, newInputValue, reason) => {
    if (reason === 'input' || reason === 'clear') {
      search(newInputValue);
      return;
    }

    latestSearch.current = newInputValue;
    setInputValue(newInputValue);
    setIsOpen(false);
    updateLoading(false);
    if (onInputChange) onInputChange(newInputValue);
  };

  const {
    InputProps: suppliedInputProps,
    inputProps: suppliedHtmlInputProps,
    ...remainingTextFieldProps
  } = textFieldProps;

  return (
    <Autocomplete
      autoComplete
      data-testid={autocompleteTestId}
      className={className}
      disabled={disabled}
      filterOptions={(availableOptions) => availableOptions}
      getOptionLabel={getAddressLabel}
      inputValue={inputValue}
      isOptionEqualToValue={(option, selectedValue) => (
        option?.id ?? option?.fullAddress ?? option?.address
      ) === (
        selectedValue?.id ?? selectedValue?.fullAddress ?? selectedValue?.address
      )}
      loading={isLoading}
      noOptionsText={inputValue.length < 3 ? 'Type at least 3 characters' : 'No options'}
      onChange={(event, newValue) => onChange(newValue)}
      onClose={() => setIsOpen(false)}
      onInputChange={handleInputChange}
      onOpen={() => {
        if (inputValue.length) setIsOpen(true);
      }}
      open={isOpen}
      options={options}
      value={value ?? null}
      renderInput={(params) => (
        <TextField
          {...params}
          {...remainingTextFieldProps}
          className={textFieldClassName}
          InputProps={{
            ...params.InputProps,
            ...suppliedInputProps,
            endAdornment: isLoading
              ? <CircularProgress size={18} className={styles.progressIcon} />
              : (suppliedInputProps?.endAdornment ?? params.InputProps.endAdornment),
          }}
          inputProps={{
            ...params.inputProps,
            ...suppliedHtmlInputProps,
            'data-testid': inputTestId,
            maxLength: 500,
          }}
          placeholder={placeholder}
          variant="outlined"
          fullWidth
        />
      )}
    />
  );
};

DomainAddressAutocomplete.defaultProps = {
  autocompleteTestId: undefined,
  className: undefined,
  disabled: false,
  inputTestId: undefined,
  onInputChange: undefined,
  onLoadingChange: undefined,
  placeholder: '',
  searchErrorMessage: 'Unable to retrieve address suggestions',
  textFieldClassName: undefined,
  textFieldProps: {},
  value: null,
};

DomainAddressAutocomplete.propTypes = {
  autocompleteTestId: PropTypes.string,
  className: PropTypes.string,
  disabled: PropTypes.bool,
  getSuggestionsAction: PropTypes.func.isRequired,
  inputTestId: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  onInputChange: PropTypes.func,
  onLoadingChange: PropTypes.func,
  placeholder: PropTypes.string,
  searchErrorMessage: PropTypes.string,
  textFieldClassName: PropTypes.string,
  textFieldProps: PropTypes.object,
  value: PropTypes.object,
};

export default DomainAddressAutocomplete;
